/**
 * Shield accounts, roles and capabilities. Shared so the server's route guards and the client's
 * UI gating are driven by the same table.
 */

export const ROLES = ['viewer', 'analyst', 'operator', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_RANK: Record<Role, number> = { viewer: 1, analyst: 2, operator: 3, admin: 4 };

export const ROLE_LABELS: Record<Role, string> = {
  viewer: 'L1 Viewer',
  analyst: 'L2 Analyst',
  operator: 'L3 Operator',
  admin: 'Admin',
};

export const CAPABILITIES = [
  { id: 'viewMap', minRole: 'viewer' },
  { id: 'viewSuspects', minRole: 'viewer' },
  { id: 'annotateSuspects', minRole: 'analyst' },
  { id: 'manageZones', minRole: 'analyst' },
  { id: 'manageThreatProfiles', minRole: 'operator' },
  { id: 'systemSettings', minRole: 'operator' },
  { id: 'manageAccounts', minRole: 'admin' },
  { id: 'impersonate', minRole: 'admin' },
] as const satisfies ReadonlyArray<{ id: string; minRole: Role }>;

export type Capability = (typeof CAPABILITIES)[number]['id'];

export function hasRole(role: Role, min: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

export function can(role: Role, capability: Capability): boolean {
  const entry = CAPABILITIES.find(c => c.id === capability);
  return !!entry && hasRole(role, entry.minRole);
}

/** What an impersonating admin may do on the target's data while "edit mode" is on: everything but account management. */
export const IMPERSONATION_EDIT_ROLE: Role = 'operator';

/**
 * The role a session acts with. An admin impersonating another account keeps operator-level rights while
 * edit mode is on (to set the account up); otherwise, and for every other session, the account's own role applies.
 */
export function effectiveRole(account: Role, actor: Role | undefined, editMode: boolean): Role {
  if (!editMode || !actor || !hasRole(actor, 'admin')) return account;
  return hasRole(account, IMPERSONATION_EDIT_ROLE) ? account : IMPERSONATION_EDIT_ROLE;
}

export const ACCOUNT_STATUSES = ['active', 'suspended'] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/** The account's linked user on the upstream data platform. */
export interface UpstreamLink {
  /** Upstream user id once provisioned or linked. */
  userId?: string;
  /** Upstream user name (`${prefix}-${username}`), used for idempotent provisioning. */
  name: string;
  /** True when the account was attached to a pre-existing upstream user instead of a provisioned one. */
  linked?: boolean;
  provisionError?: string;
  provisionedAt?: string;
}

export interface AccountDto {
  id: string;
  username: string;
  displayName: string;
  role: Role;
  status: AccountStatus;
  /** ISO 3166-1 alpha-3 codes the account may see (cache of the upstream user's countries). */
  countries: Array<string>;
  upstream: UpstreamLink;
  lastLoginAt?: string;
  createdAt: string;
}
